// ─── src/GewParticipantsView.tsx ─────────────────────────────────────────────
import { useState } from "react";
import {
  Download, Trash2, Search, X, Eye, Ticket, Users, Rocket, MapPin,
} from "lucide-react";
import {
  useGewParticipants, useGewParticipantStats, useDeleteGewParticipant,
} from "./hooks/useGew";
import { Pagination } from "./components/dashboard/Pagination";
import { RegistrationsPerDayChart } from "./components/dashboard/RegistrationsPerDayChart";
import { TableSkeleton } from "./components/ui/Skeleton";
import { StatCard } from "./components/ui/StatCard";
import type { GewParticipant } from "./api/gew";

const LIMIT = 20;

function exportCsv(rows: GewParticipant[]) {
  const esc = (v: unknown) => {
    const s = v == null ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const header = [
    "Full Name", "Gender", "Age Group", "WhatsApp", "Email",
    "Governorate", "City / Area", "Current Status", "Skills", "LinkedIn",
    "Is Founder", "Startup Name", "Startup Industry", "Startup Stage", "Startup City",
    "Submitted At",
  ].join(",");
  const body = rows.map((r) =>
    [
      r.fullName, r.gender, r.ageGroup, r.whatsapp, r.email,
      r.governorate, r.cityArea, r.currentStatus,
      (r.skills ?? []).join(" | "), r.linkedInUrl ?? "",
      r.isFounder ? "Yes" : "No",
      r.startupName ?? "", r.startupIndustry ?? "", r.startupStage ?? "", r.startupCity ?? "",
      r.createdAt,
    ].map(esc).join(",")
  );
  const blob = new Blob(["﻿" + [header, ...body].join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `gew_participants_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function GewParticipantsView() {
  const [search, setSearch] = useState("");
  const [governorate, setGovernorate] = useState("");
  const [currentStatus, setCurrentStatus] = useState("");
  const [isFounder, setIsFounder] = useState<"" | "true" | "false">("");
  const [page, setPage] = useState(1);
  const [detail, setDetail] = useState<GewParticipant | null>(null);

  const { data: stats } = useGewParticipantStats();
  const { data: allData } = useGewParticipants({ page: 1, limit: 2000 });
  const { data: tableData, isLoading, isFetching, isError } =
    useGewParticipants({ page, limit: LIMIT, search, governorate, currentStatus, isFounder });

  const { mutate: deleteRow, isPending: isDeleting } = useDeleteGewParticipant();

  const rows = tableData?.data ?? [];
  const meta = tableData?.meta ?? { total: 0, page: 1, limit: LIMIT, totalPages: 1 };
  const govOptions    = Object.keys(stats?.byGovernorate ?? {}).sort();
  const statusOptions = Object.keys(stats?.byCurrentStatus ?? {}).sort();

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Total"      value={stats?.total ?? 0}     icon={Ticket} accent="bg-brand-orange" />
        <StatCard label="Founders"   value={stats?.founders ?? 0}  icon={Rocket} accent="bg-brand-green" />
        <StatCard label="Cities"     value={govOptions.length}     icon={MapPin} accent="bg-brand-teal" />
        <StatCard label="Skills tallied" value={Object.keys(stats?.bySkills ?? {}).length} icon={Users} accent="bg-brand-teal" />
      </div>

      {/* Registrations-per-day — derived client-side from the full list the
          table already fetches (allData, up to 2 000 rows). The chart shows
          demand spikes and whether the funnel is still active. */}
      <RegistrationsPerDayChart rows={allData?.data} />

      <div className="card p-4 space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5">
          <div className="relative sm:col-span-2">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted" />
            <input
              type="text" value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search by name, email or startup…"
              className="w-full pl-9 pr-8 py-2 text-sm bg-brand-navy border border-brand-border rounded-lg text-white placeholder-brand-muted focus:outline-none focus:border-brand-green"
            />
            {search && (
              <button onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-brand-muted hover:text-white">
                <X size={14} />
              </button>
            )}
          </div>
          <select
            value={governorate}
            onChange={(e) => { setGovernorate(e.target.value); setPage(1); }}
            className="py-2 px-3 text-sm bg-brand-navy border border-brand-border rounded-lg text-white focus:outline-none focus:border-brand-green"
          >
            <option value="">All governorates</option>
            {govOptions.map((g) => <option key={g} value={g}>{g}</option>)}
          </select>
          <select
            value={currentStatus}
            onChange={(e) => { setCurrentStatus(e.target.value); setPage(1); }}
            className="py-2 px-3 text-sm bg-brand-navy border border-brand-border rounded-lg text-white focus:outline-none focus:border-brand-green"
          >
            <option value="">All statuses</option>
            {statusOptions.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select
            value={isFounder}
            onChange={(e) => { setIsFounder(e.target.value as "" | "true" | "false"); setPage(1); }}
            className="py-2 px-3 text-sm bg-brand-navy border border-brand-border rounded-lg text-white focus:outline-none focus:border-brand-green"
          >
            <option value="">Founders & non-founders</option>
            <option value="true">Founders only</option>
            <option value="false">Non-founders only</option>
          </select>
          <button
            onClick={() => exportCsv(allData?.data ?? [])}
            disabled={(allData?.data ?? []).length === 0}
            className="btn-primary text-sm flex items-center justify-center gap-1.5 disabled:opacity-50"
          >
            <Download size={14} /> Export CSV
          </button>
        </div>
      </div>

      {isLoading ? (
        <TableSkeleton />
      ) : isError ? (
        <div className="card p-8 text-center text-red-400">Failed to load participants.</div>
      ) : rows.length === 0 ? (
        <div className="card p-10 text-center text-brand-muted">No participants match these filters.</div>
      ) : (
        <>
          <div className="card overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-brand-border text-left text-brand-muted">
                  <th className="px-4 py-3 font-medium">#</th>
                  <th className="px-4 py-3 font-medium">Name</th>
                  <th className="px-4 py-3 font-medium">Contact</th>
                  <th className="px-4 py-3 font-medium">Location</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Founder</th>
                  <th className="px-4 py-3 font-medium">Submitted</th>
                  <th className="px-4 py-3 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.id} className="border-b border-brand-border/50 hover:bg-brand-border/20 transition-colors">
                    <td className="px-4 py-3 text-brand-muted">{(meta.page - 1) * LIMIT + i + 1}</td>
                    <td className="px-4 py-3 text-white whitespace-nowrap" dir="rtl">{r.fullName}</td>
                    <td className="px-4 py-3 text-brand-muted">
                      <div dir="ltr">{r.email}</div>
                      <div dir="ltr" className="text-xs">{r.whatsapp}</div>
                    </td>
                    <td className="px-4 py-3 text-brand-muted" dir="rtl">
                      <div>{r.governorate}</div>
                      <div className="text-xs">{r.cityArea}</div>
                    </td>
                    <td className="px-4 py-3 text-brand-muted" dir="rtl">{r.currentStatus}</td>
                    <td className="px-4 py-3">
                      {r.isFounder ? (
                        <span className="px-2 py-0.5 rounded-full text-xs bg-emerald-500/15 text-emerald-400">Yes</span>
                      ) : (
                        <span className="text-brand-muted text-xs">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-brand-muted whitespace-nowrap">
                      {new Date(r.createdAt).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button onClick={() => setDetail(r)} className="p-1.5 rounded-lg text-brand-muted hover:text-white hover:bg-brand-border" title="View details">
                          <Eye size={14} />
                        </button>
                        <button onClick={() => deleteRow(r.id)} disabled={isDeleting} className="p-1.5 rounded-lg text-brand-muted hover:text-red-400 hover:bg-red-500/10 disabled:opacity-40" title="Delete">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination
            page={meta.page}
            totalPages={meta.totalPages}
            total={meta.total}
            limit={LIMIT}
            onPageChange={setPage}
            isFetching={isFetching}
          />
        </>
      )}

      {detail && <DetailModal row={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

// ── Detail modal ─────────────────────────────────────────────────────────────

function DetailModal({ row, onClose }: { row: GewParticipant; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div className="card w-full max-w-2xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-brand-border">
          <div dir="rtl">
            <p className="text-white font-bold text-lg">{row.fullName}</p>
            <p className="text-brand-muted text-sm" dir="ltr">{row.email}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg text-brand-muted hover:text-white hover:bg-brand-border">
            <X size={16} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          <Grid>
            <Field label="Gender" value={row.gender} rtl />
            <Field label="Age group" value={row.ageGroup} rtl />
            <Field label="WhatsApp" value={row.whatsapp} />
            <Field label="Current status" value={row.currentStatus} rtl />
          </Grid>
          <div className="pt-4 border-t border-brand-border">
            <Grid>
              <Field label="Governorate" value={row.governorate} rtl />
              <Field label="City / area" value={row.cityArea} rtl />
              <Field label="LinkedIn" value={row.linkedInUrl ?? undefined} />
            </Grid>
          </div>
          <div className="pt-4 border-t border-brand-border">
            <p className="text-brand-muted text-xs uppercase tracking-wider mb-2">Skills</p>
            <div className="flex flex-wrap gap-2" dir="rtl">
              {(row.skills ?? []).map((s) => (
                <span key={s} className="px-2.5 py-1 rounded-lg text-xs bg-brand-teal/15 text-brand-tealLight">{s}</span>
              ))}
            </div>
          </div>
          {row.isFounder && (
            <div className="pt-4 border-t border-brand-border">
              <p className="text-brand-green text-xs uppercase tracking-wider mb-3">Startup</p>
              <Grid>
                <Field label="Name" value={row.startupName ?? undefined} rtl />
                <Field label="Industry" value={row.startupIndustry ?? undefined} />
                <Field label="Stage" value={row.startupStage ?? undefined} rtl />
                <Field label="City" value={row.startupCity ?? undefined} rtl />
              </Grid>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Grid({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">{children}</div>;
}
function Field({ label, value, rtl }: { label: string; value?: string | null; rtl?: boolean }) {
  if (!value) return null;
  return (
    <div>
      <p className="text-brand-muted text-xs uppercase tracking-wider mb-1">{label}</p>
      <p className="text-white text-sm whitespace-pre-wrap leading-relaxed" dir={rtl ? "rtl" : "ltr"}>{value}</p>
    </div>
  );
}
